/**
 * Fingerprint Pro (v4) plugin
 *
 * Loads the Pro browser agent from the official CDN via dynamic import so we
 * don't ship the SDK in our bundle (smaller initial payload + auto-updated
 * agent version). Provides `$fingerprint.identify()` for the rest of the app.
 *
 * Lazy by design (2026-06-05): the agent module is fetched but `fp.get()`
 * is NOT pre-fired at page-load — every `.get()` call is a billed Pro
 * identification, so we only spend one when the user is actually about
 * to register (LoginModal handlers → useAuth.completeBackendAuth →
 * `$fingerprint.identify()`). Anonymous browsing produces zero Pro events.
 *
 * Lazy for the main thread too (2026-08-05, #819): `start()` is what actually
 * collects the entropy (canvas / WebGL / audio / fonts) — measured at 622 ms of
 * main-thread blocking on a throttled mobile profile, ~22% of the page's total
 * blocking time and the only third-party in the top three. Deferring only
 * `get()` saved the billing but not the CPU, so `start()` no longer runs at
 * plugin setup — callers trigger it via `prepare()` or the first
 * `identify()` / `getVisitorId()`.
 *
 * Warm-up sites, so the cost never lands on the action itself:
 *   - LoginModal opening        → prepare() inline (user is reading the form)
 *   - /auth/callback mounting   → prepare() inline (overlaps the session exchange)
 *   - free-bet becoming visible → warmFingerprintWhenIdle() (see
 *     utils/warmFingerprint.ts — that one hangs off a page-load path, so it
 *     MUST be deferred to idle or it re-creates the very problem above)
 *
 * - Loader script: still fetched eagerly. `import()` is a network fetch that
 *   costs nothing on the main thread, so the module is warm by the time
 *   register is clicked; it doesn't trigger identification either.
 * - On failure (CDN down, ad-blocker, etc.) callers fall back to a random
 *   `fb_<uuid>` id (see useAuth.completeBackendAuth).
 *
 * Server-side verification: the browser returns `requestId` (event_id) from
 * the identify() call. `auth-login` forwards it to the Pro Server API for
 * canonical visitor_id resolution and Smart Signals. See spec
 * docs/superpowers/specs/2026-05-26-visitor-ips-and-universal-risk-check-design.md.
 */

// The provided object is typed `FingerprintApi`, so renaming or dropping a
// method here fails to compile rather than quietly turning every
// optional-chained call site into a no-op.
//
// On typing `$fingerprint` where it is consumed: Nuxt's inferred plugin types
// reach `.ts` files (see stores/visitor.ts, composables/useAuth.ts) but NOT SFCs
// — vue-tsc resolves it to `{}` inside a `<script setup>`, so components narrow
// locally to `WithFingerprint`. Do NOT "fix" that by augmenting NuxtApp:
// `declare module '#app'` inside this plugin makes NuxtApp reference itself as a
// base type (TS2310), and moving the same declaration into a standalone ambient
// .d.ts overrides Nuxt's generated interface instead of merging with it — both
// were tried, and both collapse EVERY other injection (`$supabase`, …) to
// `unknown` across the app.
import type { FingerprintApi } from '~/types/fingerprint'

// Type-only declaration of the Pro v4 agent — the actual module is loaded at
// runtime via a CDN dynamic import (no npm dep, no bundle bloat).
//
// Pro v4 returns snake_case fields:
//   - visitor_id    — stable per-browser identifier (this is our visitorId)
//   - event_id      — unique per-identification token (this is our requestId,
//                     server-side maps to /events/{event_id} on Server API)
//   - sealed_result — encrypted payload (unused for now)
//   - suspect_score — Pro's built-in bot/fraud heuristic (unused for now)
interface FingerprintProGetResult {
  visitor_id?: string
  event_id?: string
  sealed_result?: string | null
  suspect_score?: number
}

interface FingerprintProAgent {
  get: () => Promise<FingerprintProGetResult>
}

interface FingerprintProModule {
  start: (options: {
    apiKey?: string
    region?: string
    /** Pro v4 ingest override. Accepts string or array. When set, the
     *  agent POSTs identifications here (first-party subdomain) instead
     *  of {region}.api.fpjs.io. Loader script URL is unaffected — that
     *  still comes from fpjscdn.net. */
    endpoint?: string | string[]
  }) => Promise<FingerprintProAgent>
}

export default defineNuxtPlugin({
  name: 'fingerprint',
  parallel: true, // Don't block other plugins
  async setup() {
    const config = useRuntimeConfig()
    const apiKey = (config.public.fingerprintApiKey as string) || ''
    const region = (config.public.fingerprintRegion as string) || 'us'
    // First-party ingest subdomain (e.g. https://wagerr.aurumrun.io) that
    // CNAMEs to Fingerprint. Default comes from nuxt.config.ts. Only the
    // identification POSTs route through it; the loader script ALWAYS comes
    // from fpjscdn.net because the subdomain's Cloudflare WAF 403s any
    // /v4/<key> request. Literal '-' opts out (smoke-testing the default
    // api.fpjs.io path), trailing slash is stripped.
    const rawEndpoint = (config.public.fingerprintEndpoint as string) || ''
    const endpointBase = rawEndpoint === '-' ? '' : rawEndpoint.replace(/\/+$/, '')

    let visitorId: string | null = null
    let requestId: string | null = null

    // Build the agent loader. When apiKey is empty (local dev / preview) we
    // skip the CDN import entirely — saves a 404 in the network panel and
    // makes the fallback path the explicit default.
    const cdnUrl = apiKey ? `https://fpjscdn.net/v4/${apiKey}` : ''
    const startOptions: { region: string; endpoint?: string } = { region }
    if (endpointBase) {
      startOptions.endpoint = endpointBase
    }
    // Network only — no main-thread cost, so this stays eager (see docblock).
    const modulePromise: Promise<FingerprintProModule | null> = apiKey
      ? (import(/* @vite-ignore */ cdnUrl) as Promise<FingerprintProModule>)
          .catch((err) => {
            console.error('[fingerprint] Pro agent load failed:', err)
            return null
          })
      : Promise.resolve(null)

    // `start()` is the expensive half (entropy collection, #819). Memoised so
    // concurrent callers share one agent: `agentPromise` is assigned before the
    // first await, so two callers racing here provably share one `start()`.
    //
    // A FAILED start clears the memo so the next caller retries once. The cache
    // exists to avoid duplicate work, not to protect CPU — a start() that failed
    // because the network dropped never spent any. Caching the failure would
    // strand the whole session on `fb_<uuid>`, which downstream surfaces as
    // FP_UNAVAILABLE and tells the user to disable their ad blocker (see
    // FpFallbackModal) even when the real cause was a momentary blip.
    //
    // Retries stay bounded in practice: a module that never loaded (ad blocker,
    // CDN down — the common failure) resolves to null WITHOUT entering this
    // catch, so it is cached and never retried. Only a module that loaded and
    // whose start() then rejected gets another attempt, and those come from
    // discrete surfaces (login modal, free-bet warm-up, OAuth callback).
    let agentPromise: Promise<FingerprintProAgent | null> | null = null
    const getAgent = (): Promise<FingerprintProAgent | null> => {
      if (!agentPromise) {
        agentPromise = modulePromise
          .then((mod) => (mod ? mod.start(startOptions) : null))
          .catch((err) => {
            console.error('[fingerprint] Pro agent start failed:', err)
            agentPromise = null
            return null
          })
      }
      return agentPromise
    }

    // Resolve fingerprint async. Memoized — Pro's identification call is
    // billed per unique request, so we only call .get() once per session and
    // reuse the resulting visitorId.
    const resolveFingerprint = async (): Promise<string> => {
      if (visitorId) return visitorId

      const fp = await getAgent()
      if (!fp) {
        // No agent (no key, CDN/ad-blocker failure) — fall back to a stable
        // per-session random id. Functional but not abuse-resistant; the
        // backend's identity-risk check handles the rest via stored IPs.
        const fallback = `fb_${crypto.randomUUID()}`
        visitorId = fallback
        return fallback
      }

      try {
        const result = await fp.get()
        // Pro v4 SDK returns snake_case fields. Map to our externally-stable
        // camelCase contract:
        //   result.visitor_id → visitorId
        //   result.event_id   → requestId (the value the Server API expects
        //                                   on /events/{request_id})
        // Guard against missing fields (SDK shape changes / loader errors)
        // by falling through to the random fallback id below.
        if (!result?.visitor_id) {
          throw new Error('Pro get() returned no visitor_id')
        }
        visitorId = result.visitor_id
        requestId = result.event_id ?? null
        return visitorId
      } catch (err) {
        console.error('[fingerprint] Pro get() failed:', err)
        const fallback = `fb_${crypto.randomUUID()}`
        visitorId = fallback
        return fallback
      }
    }

    // Clean up any previously cached visitor ID from localStorage — old OSS
    // v3 plugin had no localStorage, but downstream code may have written
    // there before. Safe to remove on every boot.
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('aurumrun_visitor_id')
    }

    // NOTE: we intentionally pre-fire NEITHER getAgent() nor
    // resolveFingerprint() here. Two separate reasons, don't collapse them:
    //   - `get()` is billed per identification, so auto-resolving at page load
    //     would charge a Pro event for every visit including users who never
    //     register.
    //   - `start()` blocks the main thread for ~620 ms on a mid-tier phone
    //     (#819), landing mid-load right when the user makes their first tap.
    // Callers invoke identify() / getVisitorId() on demand at the register
    // click — see useAuth.completeBackendAuth — and getAgent() runs then.

    /**
     * Force a fresh Pro identification call and return the (visitorId,
     * requestId) pair. The Pro Server API rejects identifications older
     * than ~60s ("stale identification"), so callers that submit the
     * requestId to backend (auth-login, visitor-free-round, free-bet grant
     * confirms) MUST call this RIGHT BEFORE the API submit — NOT reuse
     * cached values from page-load.
     *
     * On any failure (no agent, fp.get() error) returns null — caller can
     * fall back to the cached visitorId and skip the requestId field.
     */
    const identify = async (): Promise<{ visitorId: string; requestId: string } | null> => {
      const fp = await getAgent()
      if (!fp) return null
      try {
        const result = await fp.get()
        if (!result?.visitor_id || !result?.event_id) return null
        visitorId = result.visitor_id
        requestId = result.event_id
        return { visitorId: result.visitor_id, requestId: result.event_id }
      } catch (err) {
        console.error('[fingerprint] Pro identify() failed:', err)
        return null
      }
    }

    const api: FingerprintApi = {
          /**
           * Get the visitor ID. Awaits the agent if not yet ready;
           * returns the cached value on subsequent calls. Use for UI
           * rendering (Pro bills per identification — don't waste calls
           * on display-only paths). For verified API submits use
           * `identify()` instead.
           */
          getVisitorId: resolveFingerprint,

          /**
           * Get the synchronously-cached visitor ID. Returns null until
           * the agent has resolved at least once. Use for fire-and-forget
           * paths that mustn't await.
           */
          getCachedVisitorId: (): string | null => visitorId,

          /**
           * Force a FRESH Pro identification call. Returns
           * `{visitorId, requestId}` (event_id) with a brand-new
           * server-verifiable timestamp. Call this right before any
           * API submit that includes `fingerprintRequestId`.
           */
          identify,

          /**
           * Warm the agent — runs `start()` (the ~620 ms entropy collection,
           * #819) without billing an identification. Returns the promise so a
           * caller CAN await it, but every current call site is
           * fire-and-forget.
           *
           * Call this from any surface that is about to become able to reach
           * identify(), so the cost lands on idle time instead of the action:
           *   - LoginModal opening        → register / OAuth submit
           *   - free-bet becoming visible → placeFreeRound() on the bet click
           *   - /auth/callback mounting   → completeOAuthLogin()
           * Memoised, safe to call repeatedly.
           */
          prepare: (): Promise<FingerprintProAgent | null> => getAgent(),

          /**
           * Get the request ID from the most recent agent call. Pair this
           * with visitorId when calling backend endpoints so server-side
           * Pro verification (future) can validate the identification was
           * genuine. Returns null until the agent has resolved at least once.
           */
      getRequestId: (): string | null => requestId,
    }

    return { provide: { fingerprint: api } }
  },
})
